import React, { useEffect, useState } from 'react';
import { fetchBoatOptions } from './api';
import { OTHER } from './constants';
import {
  CHARTER_COMPANY_LOGO_URL_BY_KEY,
  DEFAULT_YACHT_CLUB_FLAG_URL,
  YACHT_CLUB_FLAG_URL_BY_KEY,
} from './boatAssetMap';

const EMPTY_BOAT_CATALOG = {
  charterCompanies: [],
  yachtClubs: [],
};
const BOAT_CATALOG_CACHE_TTL_MS = 60 * 1000;

const CHARTER_COMPANY_PLACEHOLDER_OPTION = {
  key: '',
  value: 'Please select an option',
  logoUrl: '',
  websiteUrl: '',
  disabled: true,
};

const OTHER_CHARTER_COMPANY_OPTION = {
  key: OTHER,
  value: 'Other',
  logoUrl: '',
  websiteUrl: '',
  disabled: false,
};

const YACHT_CLUB_PLACEHOLDER_OPTION = {
  key: '',
  value: 'Select a Yacht Club',
  flagUrl: '',
  websiteUrl: '',
  disabled: true,
};

let cachedBoatCatalog = null;
let cachedBoatCatalogTimestamp = 0;
let pendingBoatCatalogRequest = null;

const isBoatCatalogCacheFresh = () => {
  return (
    Boolean(cachedBoatCatalog) &&
    Date.now() - cachedBoatCatalogTimestamp < BOAT_CATALOG_CACHE_TTL_MS
  );
};

const normalizeText = value => String(value || '').trim();

const normalizeKey = value => normalizeText(value).toLowerCase();

const dedupeByKeyOrSlug = items => {
  const seenKeys = new Set();
  const seenSlugs = new Set();

  return (items || []).reduce((acc, item) => {
    const key = normalizeKey(item?.key);
    const slug = normalizeKey(item?.slug);

    if (!key || seenKeys.has(key) || (slug && seenSlugs.has(slug))) {
      return acc;
    }

    seenKeys.add(key);
    if (slug) {
      seenSlugs.add(slug);
    }

    return [...acc, item];
  }, []);
};

export const createYachtClubDisplayLabel = ({ name, city, state }) => {
  const safeName = normalizeText(name);
  const location = [normalizeText(city), normalizeText(state)].filter(Boolean).join(', ');
  return location ? `${safeName} (${location})` : safeName;
};

export const getYachtClubSavedValue = value => {
  if (value == null) {
    return '';
  }

  if (typeof value === 'object') {
    const rawValue =
      value.clubId ??
      value.key ??
      value.slug ??
      value.name ??
      value.value ??
      value.label ??
      value.displayName;

    return typeof rawValue === 'object'
      ? getYachtClubSavedValue(rawValue)
      : normalizeText(rawValue);
  }

  return normalizeText(value);
};

export const getYachtClubOptionLabel = option => {
  return normalizeText(
    option?.label ||
      option?.displayName ||
      option?.value ||
      option?.name ||
      getYachtClubSavedValue(option)
  );
};

const getYachtClubLookupCandidates = option => {
  return [
    option?.key,
    option?.slug,
    option?.name,
    option?.value,
    option?.label,
    option?.displayName,
  ]
    .map(normalizeKey)
    .filter(Boolean);
};

export const getYachtClubSearchText = option => {
  return normalizeKey(
    [
      option?.searchText,
      option?.key,
      option?.slug,
      option?.name,
      option?.value,
      option?.label,
      option?.displayName,
      option?.city,
      option?.state,
    ]
      .filter(Boolean)
      .join(' ')
  );
};

export const createUnknownYachtClubOption = value => {
  const label = getYachtClubOptionLabel(value);
  const key = getYachtClubSavedValue(value) || label;

  if (!label && !key) {
    return null;
  }

  return {
    id: null,
    key,
    value: label || key,
    name: label || key,
    label: label || key,
    displayName: label || key,
    slug: '',
    websiteUrl: normalizeText(value?.websiteUrl),
    flagUrl: DEFAULT_YACHT_CLUB_FLAG_URL,
    disabled: false,
    isUnknown: true,
  };
};

const normalizeCharterCompany = item => {
  const key = normalizeKey(item?.key || item?.name);
  const value = normalizeText(item?.name || item?.value);
  if (!key || !value) {
    return null;
  }

  return {
    id: item?.id || null,
    key,
    value,
    slug: normalizeText(item?.slug),
    websiteUrl: normalizeText(item?.websiteUrl),
    logoUrl: normalizeText(item?.logoUrl) || CHARTER_COMPANY_LOGO_URL_BY_KEY[key] || '',
    disabled: false,
  };
};

const normalizeYachtClub = item => {
  const key = normalizeKey(item?.key || item?.slug || item?.name);
  const value = normalizeText(item?.name || item?.value);
  if (!key || !value) {
    return null;
  }

  const city = normalizeText(item?.city);
  const state = normalizeText(item?.state);
  const label =
    normalizeText(item?.label || item?.displayName) ||
    createYachtClubDisplayLabel({ name: value, city, state });
  const sourceFlagUrl = normalizeText(item?.flagUrl || item?.flag);
  const flagUrl = sourceFlagUrl || YACHT_CLUB_FLAG_URL_BY_KEY[key] || DEFAULT_YACHT_CLUB_FLAG_URL;

  return {
    id: item?.id || null,
    key,
    name: value,
    value,
    label,
    displayName: label,
    slug: normalizeText(item?.slug),
    websiteUrl: normalizeText(item?.websiteUrl),
    flagUrl,
    city,
    state,
    searchText: [value, label, city, state, key, item?.slug].filter(Boolean).join(' '),
    disabled: false,
  };
};

export const normalizeBoatCatalog = response => {
  const charterCompanies = dedupeByKeyOrSlug(
    (Array.isArray(response?.charterCompanies) ? response.charterCompanies : [])
      .map(normalizeCharterCompany)
      .filter(Boolean)
  );
  const yachtClubs = dedupeByKeyOrSlug(
    (Array.isArray(response?.yachtClubs) ? response.yachtClubs : [])
      .map(normalizeYachtClub)
      .filter(Boolean)
  ).sort((a, b) =>
    getYachtClubOptionLabel(a).localeCompare(getYachtClubOptionLabel(b), 'en', {
      sensitivity: 'base',
    })
  );

  return {
    charterCompanies,
    yachtClubs,
  };
};

const loadBoatCatalog = async () => {
  if (isBoatCatalogCacheFresh()) {
    return cachedBoatCatalog;
  }

  if (!pendingBoatCatalogRequest) {
    pendingBoatCatalogRequest = fetchBoatOptions({})
      .then(response => {
        cachedBoatCatalog = normalizeBoatCatalog(response);
        cachedBoatCatalogTimestamp = Date.now();
        return cachedBoatCatalog;
      })
      .finally(() => {
        pendingBoatCatalogRequest = null;
      });
  }

  return pendingBoatCatalogRequest;
};

export const findCharterCompanyOption = (charterCompanies, key) => {
  const normalizedKey = normalizeKey(key);
  if (!normalizedKey) {
    return null;
  }

  return (charterCompanies || []).find(item => normalizeKey(item?.key) === normalizedKey) || null;
};

export const findYachtClubOption = (yachtClubs, key) => {
  const normalizedKey = normalizeKey(getYachtClubSavedValue(key));
  if (!normalizedKey) {
    return null;
  }

  return (
    (yachtClubs || []).find(item => getYachtClubLookupCandidates(item).includes(normalizedKey)) ||
    null
  );
};

export const resolveYachtClubOption = (yachtClubs, value) => {
  return findYachtClubOption(yachtClubs, value) || createUnknownYachtClubOption(value);
};

export const normalizeYachtClubAffiliations = yachtClubs => {
  const items = Array.isArray(yachtClubs) ? yachtClubs : yachtClubs ? [yachtClubs] : [];

  return items
    .map(item => {
      const rawClubValue = item?.clubId !== undefined ? item.clubId : item;
      const clubId = getYachtClubSavedValue(rawClubValue);

      if (!clubId) {
        return null;
      }

      return {
        ...(typeof item === 'object' && item != null ? item : {}),
        clubId,
      };
    })
    .filter(Boolean);
};

export const getCharterCompanySelectOptions = charterCompanies => {
  return [
    CHARTER_COMPANY_PLACEHOLDER_OPTION,
    ...(charterCompanies || []),
    OTHER_CHARTER_COMPANY_OPTION,
  ];
};

export const getYachtClubSelectOptions = yachtClubs => {
  return [YACHT_CLUB_PLACEHOLDER_OPTION, ...(yachtClubs || [])];
};

export const useBoatCatalog = () => {
  const [state, setState] = useState(() => ({
    boatCatalog: cachedBoatCatalog || EMPTY_BOAT_CATALOG,
    loading: !cachedBoatCatalog,
    error: null,
  }));

  useEffect(() => {
    let cancelled = false;

    loadBoatCatalog()
      .then(boatCatalog => {
        if (cancelled) {
          return;
        }

        setState({
          boatCatalog,
          loading: false,
          error: null,
        });
      })
      .catch(error => {
        if (cancelled) {
          return;
        }

        setState(currentState => ({
          boatCatalog: currentState.boatCatalog,
          loading: false,
          error,
        }));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    ...state.boatCatalog,
    loading: state.loading,
    error: state.error,
  };
};

export const withBoatCatalog = WrappedComponent => {
  const WithBoatCatalog = props => {
    const boatCatalog = useBoatCatalog();
    return <WrappedComponent {...props} boatCatalog={boatCatalog} />;
  };

  WithBoatCatalog.displayName = `withBoatCatalog(${WrappedComponent.displayName ||
    WrappedComponent.name ||
    'Component'})`;

  return WithBoatCatalog;
};
